import { Redirect, router } from 'expo-router';
import { useAtom } from 'jotai/index';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    FruitfulCustomIcon,
    FullScreenSpinner,
    InstitutionLogo,
    Separator,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { SelectableRow } from '../../../../../bills/components/common/BillRows';
import { useAuthedUser } from '../../../../../common/providers/AuthedUserProvider';
import useGetAllBankAccountDetailsFromGlobalState from '../../../../hooks/useGetAllBankAccountDetailsFromGlobalState';
import useGetInternalAndExternalDepositAccounts from '../../../../hooks/useGetInternalAndExternalDepositAccounts';
import { selectedIncomeRuleAtomLegacy } from '../../../../state/PaycheckRoutingAtoms';
import type { BankAccountDisplayDetail } from '../../../../types/BankAccount';
import { PaycheckRoutingRuleAccountType } from '../../../../types/PaycheckRouting';
import IncomeSplitSubscreenWrapper from './IncomeSplitSubscreenWrapper';

const EditAccount = () => {
    const user = useAuthedUser();
    const { bankAccountDetails } = useGetAllBankAccountDetailsFromGlobalState();
    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetInternalAndExternalDepositAccounts({});
    const [selectedRule, setSelectedRule] = useAtom(
        selectedIncomeRuleAtomLegacy,
    );

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );
    const prevAccountId = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        if (prevAccountId.current !== undefined) {
            // new account selected, move back
            router.back();
        } else {
            prevAccountId.current = selectedRule?.accountId ?? null;
        }
    }, [selectedRule?.accountId]);

    if (isLoadingDepositAccounts) {
        return <FullScreenSpinner />;
    }

    if (!selectedRule) {
        return <Redirect href="/" />;
    }

    return (
        <IncomeSplitSubscreenWrapper header="Account">
            <YStack separator={<Separator />}>
                {[
                    ...bankAccountDetails.filter(
                        (account) =>
                            account.primaryUser.id === user.id ||
                            account.jointUser?.id === user.id,
                    ),
                    ...externalAccounts,
                ].map((accountDetails, i) => {
                    const { icon, color, nickname, accountNumberMasked } =
                        accountDetails;

                    const isExternal =
                        'isExternal' in accountDetails &&
                        accountDetails?.isExternal;
                    const accountId = isExternal
                        ? accountDetails.id
                        : (accountDetails as BankAccountDisplayDetail)
                              .bankAccountId;
                    const isSelected = selectedRule.id === accountId;

                    return (
                        <View paddingVertical={8} key={i}>
                            <SelectableRow
                                isSelected={isSelected}
                                isMultiSelect={false}
                                isFirstRow={i === 0}
                                onSelect={() => {
                                    if (isSelected) {
                                        router.back();
                                    } else {
                                        setSelectedRule({
                                            ...selectedRule,
                                            accountId,
                                            isExternal: isExternal ?? false,
                                            amount: 0,
                                            accountType: isExternal
                                                ? PaycheckRoutingRuleAccountType.EXTERNAL
                                                : PaycheckRoutingRuleAccountType.INTERNAL_DEPOSITORY,
                                        });
                                    }
                                }}
                                rowContentComponent={
                                    <XStack alignItems="center" gap={16}>
                                        {isExternal ? (
                                            <InstitutionLogo
                                                logo={
                                                    accountDetails.logo ??
                                                    accountDetails.imgBase64
                                                }
                                            />
                                        ) : (
                                            <FruitfulCustomIcon
                                                customSize={{
                                                    iconSize: 20,
                                                    padding: 14,
                                                }}
                                                icon={icon}
                                                color={color}
                                            />
                                        )}
                                        <YStack gap={4}>
                                            <BodyMediumRegular>
                                                {isExternal
                                                    ? accountDetails.accountType
                                                    : nickname}
                                            </BodyMediumRegular>
                                            {isExternal ? (
                                                <BodySmallRegular color="$gray1000">
                                                    {accountDetails.bankName} *
                                                    {accountNumberMasked.slice(
                                                        -5,
                                                    )}
                                                </BodySmallRegular>
                                            ) : (
                                                <BodySmallRegular color="$gray1000">
                                                    Fruitful{' '}
                                                    {accountNumberMasked.slice(
                                                        -5,
                                                    )}
                                                </BodySmallRegular>
                                            )}
                                        </YStack>
                                    </XStack>
                                }
                            />
                        </View>
                    );
                })}
            </YStack>
        </IncomeSplitSubscreenWrapper>
    );
};

export default EditAccount;
